import moment from 'dayjs'
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { Location } from 'components/BillingLocationSelector'

import * as ls from 'utils/localStorage'
import Api from "../api";
import {useHistory} from "react-router";
import {usePersistedApi, usePersistedState} from "../hooks";
import entities from "../reduxStore/entityKeys";
import { HygienistRole, RELIABILITY_STATUSES, roles } from "./constants";
import { OfferHygienist, SkillsProps, HygienistProps } from "./types";
import { formatTalentDisplayName } from "./formatTalentDisplayName";

export { formatTalentDisplayName };

moment.extend(utc);
moment.extend(timezone);
moment.extend(customParseFormat);

const mainView = (window as any).mainView
// @ts-ignore
const app = window.app;
const TempStars = (window as any).TempStars

export const hexWithAlpha = (hexColor: string, alpha: number) => {
	const rgb =
		(hexColor.match(/#(..)(..)(..)/) as any)
			.slice(1)
			.map((a: any) => Number.parseInt(a, 16))
			.join(', ')
	return `rgba(${rgb}, ${alpha})`
}

export const formatPrice = (price: number) => {
	return `$${(price / 100).toFixed(2)}`
}

export const getEnumKeys = (en: any) => {
	return Object
		.keys(en)
		.filter(k => typeof en[k as any] === 'number') as Array<string>
}

export const getEnumValues = (en: any) => {
	return getEnumKeys(en)
		.map(k => en[k as any]) as Array<number>
}

export const exitReactStyles = () => {
	const react = document.getElementById('react')
	const noReact = document.getElementById('noReact')
	if (react) react.style.display = 'none'
	if (noReact) noReact.style.display = 'block'

	window.removeEventListener('storage', () => {
		ls.remove('pageData')
	})
}

export const exitReact = (page?: string | null, props?: any, direction?: 'fwd' | 'bwd' | 'rld', data?: any) => {
	try {
		const userAccount: {
			hygienistId: number | null,
			dentistId: number | null
		} = ls.get('userAccount') || {}

		let reactPage = page || 'landing';
		if (page === 'home') {
			reactPage = Number.isInteger(userAccount?.hygienistId) ? 'hygienistHome' : 'dentistHome';
		}

		TempStars.App.goReact(reactPage, props);
	} catch (e: any) {
		console.log(e);
	}
}

export const navigateToCordovaLocationsScreen = () => {
	// The develop branch uses the React add-location flow. Keep this helper
	// name for existing call sites, but route to the registered React page.
	try {
		TempStars.App.goReact('locationAdd')
	} catch {
	}
}

export const navigateToCordovaPaymentInfoScreen = (location: Location, paymentPurpose: 'revenue' | 'native' = 'revenue') => {
	try {
		eval(`TempStars.Dentist.Router.goForwardPage('payment-info', { locationId: ${location.id}, paymentPurpose: '${paymentPurpose}' });`)
	} catch {
	}
}

export const mapJobTypeToPosition = (jobType: number): string => {
	return [
		'Hygienist',
		'Dental Assistant',
		'Admin/Front Desk',
		'Associate Dentist',
	][jobType]
}

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export const formatDate = (date: Date | string, fmt = 'MMMM DD, YYYY', timeZone?: string) => {
	if (date) {
	  const parsedDate = dayjs.utc(date);
	  
	  if (!parsedDate.isValid()) {
		console.error('Invalid date input:', date);
		return '';
	  }
  
	  if (timeZone) {
		return parsedDate.tz(timeZone, true).format(fmt);
	  }
	  
	  return parsedDate.format(fmt);
	}
	return '';
  };

export const getDaysFromDate = (date: Date | string): number => {
	const diff = new Date().getTime() - new Date(date).getTime();
	return Math.round(diff / 86400000)
}

export const formatDateBackend = (date: Date) => {
	if (!date) {
		return null as any
	}
	return moment(date).format('YYYY-MM-DD HH:mm:ss')
}

export const formatAvailabilityToNames = (availability: string | Array<string>) => {
	if (typeof availability === 'string') {
		availability = availability.split(',')
	}
	const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
	let resultDays: string[] = []
	availability.forEach((day, index) => {
		if (day === '1') {
			return resultDays.push(days[index])
		}
	})
	return resultDays.join(', ')
}

export const formatCertificates = (isOrtho ?: boolean, isRestorative ?: boolean): string => {
	let result = []
	if (isOrtho) {
		result.push('Orthodontic')
	}
	if (isRestorative) {
		result.push('Restorative')
	}
	return result.join(' & ')
}

export const formatCityProvince = (city: string, province: string): string => {
	return `${city}, ${province}`
}

export const formatFullLocation = (location?: Location): string => {
	return [location?.address, location?.city, location?.province].filter(Boolean).join(', ');
}

export const mimeTypeToExtension = (type: string): string => {
	switch (type) {
		case 'application/msword':
			return 'doc';
		case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
			return 'docx';
		case 'application/pdf':
			return 'pdf';
		case 'application/rtf':
		case 'text/richtext':
			return 'rtf';
		case 'text/plain':
			return 'txt';
		case 'image/jpeg':
		case 'image/jpg':
			return 'jpg';
		case 'image/gif':
			return 'gif';
		case 'image/png':
			return 'png';
		case 'image/webp':
			return 'webp';
		case 'image/heic':
		case 'image/heif':
			return 'heic';
		case 'text/calendar':
			return 'ics';
		default:
			return '';
	}
};

export const login = async ( email: string, password: string, setUser: (value: any) => any ) => {
	TempStars.Push?.setNavigationReady?.(false);
	await TempStars.User.releasePushRegistration?.();
	TempStars.User.userLoggedIn = false;
	TempStars.User.userAccount = undefined;
	ls.remove('userAuth');
	ls.remove('userAccount');
	ls.remove('skillsModalSeen');
	
	// Clear the Api authorization header to prevent stale token from being sent with login request
	// This ensures fresh login attempts don't fail due to old session data
	Api.setAuthJwtToken(undefined);
	
	try {
		const auth = await Api.login( email, password );
		let userAuth = {
			jwtToken: auth.token
		};
		ls.set('userAuth', userAuth);
		TempStars.Api.setAuthJwtToken(auth.token);
		await Api.updateToken()
		const user = await Api.getUser();
		setUser(user)
		TempStars.User.setCurrentUser(user);
		ls.set( 'userAccount', user );
		if ( auth.isAgent ) {
			TempStars.Analytics.track('Support Agent Logged In')
			ls.set( 'isAgent', auth.isAgent );
		}
		TempStars.User.setUserLoggedIn(true);
		await TempStars.Push.init();
		await TempStars.User.updateRegistration(true);
		await TempStars.User.setUserAnalytics(user)
		await TempStars.Conversations.getUserChatAccessToken(true)
		await TempStars.Conversations.createConversationClient()
		return user;
	} catch (e: any) {
		TempStars.Logging.log('login failed for: ' + email );
		throw e;
	}
}

export { checkIsSetupComplete } from './profileSetup';

export const sendResume = async (resumeSrc: string, resumeUrl: any, userId: number, errorMessage: any, setErrorMessage: any) => {
	const file = resumeUrl.files[0];
	const ext = mimeTypeToExtension(file.type)

	const dotIndex = file.name.lastIndexOf('.');
	if ( dotIndex != -1 && file.name.length > dotIndex ) {
		console.log('File extension is OK')
	} else {
		setErrorMessage({...errorMessage, resume: 'Resume Error. Please change or remove file'})
	}
	try {
		const blob = TempStars.File.convertDataURItoBlobSync( resumeSrc );
		const result = await TempStars.Api.getAwsUploadCredentials(file.type, userId + '.' + ext);
		await TempStars.Ajax.upload(result.signedRequest, blob, { contentType: file.type })
		return result.url;
	} catch (e: any) {
		return setErrorMessage({...errorMessage, resume: e?.message || 'Error'})
	}
}

export const uploadPhoto = async (imageSrc: string, photoUrl: any, userId: number, errorMessage: any, setErrorMessage: any) => {
	const file = photoUrl.files[0];
	const ext = mimeTypeToExtension(file.type)
	if (!ext.length) {
		setErrorMessage({...errorMessage, photo: 'unknown image type'})
	}

	try {
		const blob = TempStars.File.convertDataURItoBlobSync(imageSrc);
		const result = await TempStars.Api.getAwsUploadCredentials(file.type, userId + '.' + ext)
		await TempStars.Ajax.upload(result.signedRequest, blob, {contentType: file.type})
		return result.url
	} catch (e: any) {
		return setErrorMessage({...errorMessage, photo: 'unknown image type'})
	}
}

export const addUniqueToArray = (array: any[], array2: any[]) => {
	const arrSet = new Set([...array, ...array2]);
	return Array.from(arrSet)
}

export const getTypeByRoleNumber = (roleNumber: number) => {
	let type;
	switch (roleNumber) {
		case roles.ADMIN: //ADMIN
			type = 2; //ADMIN
			break;
		case roles.ASSISTANT://ASSISTANT
			type = 1 //ASSISTANT
			break;
		case roles.FRONT_DESK: //FRONT_DESK
			type = 2; //FRONT_DESK
			break;
		case roles.ASSOCIATE_DENTIST: //ASSOCIATE_DENTIST
			type = 3; //ASSOCIATE_DENTIST
			break;
		default:
			type = 0; //HYGIENIST
	}
	return type;
}

type HygienistType = HygienistProps['type'];
export function getRoleByTypeNumber(typeNumber: HygienistType): HygienistRole {
	let role: HygienistRole;
	switch (typeNumber) {
		case 2: //ADMIN
			role = roles.FRONT_DESK; //ADMIN/FRONT_DESK?
			break;
		case 1://ASSISTANT
			role = roles.ASSISTANT //ASSISTANT
			break;
		case 3: //ASSOCIATE_DENTIST
			role = roles.ASSOCIATE_DENTIST; //ASSOCIATE_DENTIST
			break;
		default:
			role = roles.HYGIENIST; //HYGIENIST
	}
	return role;
}

export const formatProfileHygienistName = (
	hygienist: OfferHygienist,
	skills: SkillsProps[],
	options?: { layout?: 'inline' | 'stacked' },
) => {
	const layout = options?.layout || 'inline';
	const legal = `${hygienist.firstName || ''} ${hygienist.lastName || ''}`.trim();
	const preferred = (hygienist.preferredName || '').trim();
	let suffix = '';
	let qualificationSkill = skills?.find(skill => skill.qualification === 1)
	if (qualificationSkill) {
		suffix = `, ${qualificationSkill.name}`;
	} else {
		qualificationSkill = skills?.find(skill => skill.license === 1)
	}

	if (qualificationSkill) {
		const cred = qualificationSkill.name === 'HFTDA' || qualificationSkill.name === 'NHFTDA'
			? qualificationSkill.label
			: qualificationSkill.name;
		suffix = `, ${cred}`;
	} else if (hygienist.type === 0) {
		suffix = ', RDH';
	} else if (hygienist.type === 1) {
		suffix = ', DA';
	}

	if (!preferred) {
		return `${legal}${suffix}` || undefined;
	}
	if (layout === 'stacked') {
		return `${legal}${suffix}\n(${preferred})`;
	}
	return `${legal} (${preferred})${suffix}`;
}

export const getPaymentTerms = (paymentTerms:number) => {
	if(paymentTerms===1) return 'Within 24h'
	if(paymentTerms===5) return 'Within 5 Days'
	if(paymentTerms===14) return 'Within 2 weeks'
	if(paymentTerms===30) return 'Within a month'
}

export const getPaymentMethod = (paymentMethod: number, country: number) => {
	if(paymentMethod===1) return 'Pay Now (TempStars\'s Payment Processor)'
	if(paymentMethod===2) return country === 0 ? 'Cheque' : 'Check'
	if(paymentMethod===3) return 'Electronic Funds Transfer'
	if(paymentMethod===4) return 'Payroll / Temporary Employee'
}

/** T2-6952: DO Posted Shift / Custom Offers payment terms line (omit "expected via" when method unset). */
export const formatDentistPaymentTermsWithMethod = (
	paymentTerms: number | null | undefined,
	paymentMethod?: number | null,
	country?: number | null,
): string => {
	const terms = getPaymentTerms(Number(paymentTerms)) || '';
	if (!paymentMethod) {
		return terms;
	}
	const method = getPaymentMethod(Number(paymentMethod), Number(country ?? 0));
	if (!method) {
		return terms;
	}
	return terms ? `${terms} expected via ${method}` : `Expected via ${method}`;
}

/** T2-6952: agreement phrase used next to Late Payment Policy link (PostJobConfirmation style). */
export const getLatePaymentAgreementPrefix = (
	jobType: number | null | undefined,
	paymentTerms: number | null | undefined,
): string => {
	const roleString =
		jobType === 0 ? 'Dental Hygienist'
			: jobType === 1 ? 'Dental Assistant'
				: 'Reception/Admin';
	let paymentTerm: string;
	switch (Number(paymentTerms)) {
		case 5:
			paymentTerm = 'within 5 days of';
			break;
		case 14:
			paymentTerm = 'within two weeks of';
			break;
		case 30:
			paymentTerm = 'within one month of';
			break;
		case 1:
		default:
			paymentTerm = 'within 24 hours of';
			break;
	}
	return `You agree to pay your ${roleString} ${paymentTerm} the shift. `;
}

export const getPaymentType = (payType: number) => {
	if(payType===0) return '1099 Independent Contractor'
	if(payType===1) return 'W2'
	if(payType===2) return 'Either 1099 Independent Contractor or W2'
}

/**
 * @deprecated Prefer the authoritative percentile-based `reliabilityLabel`
 * returned by the API (see `/hygienists/me/membership-status`). This helper
 * maps a raw `weightedReliability` score (0–1) to a coarse band and is only
 * retained for local "projected" previews that compute a hypothetical new
 * score client-side (e.g. the cancel-shift flow). Use `resolveReliabilityLabel`
 * when you have both an API label and a raw score.
 */
export const getReliabilityStatus = (score: number) => {
	if(score > 0.9 ) return RELIABILITY_STATUSES['90']
	if(score > 0.8 ) return RELIABILITY_STATUSES['80']
	if(score > 0.7 ) return RELIABILITY_STATUSES['70']
	if(score > 0.6 ) return RELIABILITY_STATUSES['60']
	return RELIABILITY_STATUSES['50']
}

/**
 * @deprecated Prefer the percentile-based API label + `getPercentileLabelColor`
 * in MyStatsPage/utils.ts. Kept for parity with `getReliabilityStatus` for
 * local projected previews. Colors and titles are aligned with the band
 * naming in `RELIABILITY_STATUSES`.
 */
export const getReliabilityStatusObject = (score: number) => {
	if(score > 0.9 ) return { title: 'Outstanding', position: '10', color: '#13CA82' }
	if(score > 0.8 ) return { title: 'Great', position: '20', color: '#13CA82' }
	if(score > 0.7 ) return { title: 'Good', position: '30', color: '#0B4598' }
	if(score > 0.6 ) return { title: 'Fair', position: '40', color: '#F59E0B' }
	return { title: 'Low', position: '50', color: '#FB7672' }
}

/**
 * Prefers the authoritative percentile-based API label when available, and
 * falls back to the raw-score band only when no API label exists (e.g. a
 * hypothetical "projected" score computed client-side). This is the
 * recommended way to render reliability / feedback labels going forward.
 */
export const resolveReliabilityLabel = (
	apiLabel: string | null | undefined,
	rawScore: number | null | undefined,
): string => {
	if (apiLabel) return apiLabel;
	if (rawScore == null || !Number.isFinite(rawScore)) return 'Not yet rated';
	return getReliabilityStatus(rawScore);
}

export const formatErrorMessage = (error: any) => {
	try {
		return JSON.parse(error?.message)?.message
	} catch (e) {
		return error?.message
	}
}

export const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Radius of the Earth in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180; // Convert degrees to radians
    const dLon = (lon2 - lon1) * Math.PI / 180; // Convert degrees to radians
    const a = 
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
        Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
    const distance = R * c; // Distance in kilometers
    return distance;
};
